import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useProfileSafe } from '@/contexts/UserProfileContext';

interface StudioState {
  resumePath: string | null;
  resumeAnalysis: any | null;
  selectedCareerPaths: any[];
  skillGapAnalysis: any | null;
  isInitialized: boolean;
}

interface StudioContextType extends StudioState {
  setResumePath: (path: string | null) => void;
  setResumeAnalysis: (analysis: any) => void;
  setSelectedCareerPaths: (paths: any[]) => void;
  setSkillGapAnalysis: (analysis: any) => void;
  getAuthToken: () => string | null;
  profileId: string | null;
}

const StudioContext = createContext<StudioContextType | null>(null);

export const useStudio = () => {
  const ctx = useContext(StudioContext);
  if (!ctx) throw new Error('useStudio must be used within StudioProvider');
  return ctx;
};

export const StudioProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { profile } = useProfileSafe();
  const profileId = profile?.id || null;

  const [state, setState] = useState<StudioState>({
    resumePath: null,
    resumeAnalysis: null,
    selectedCareerPaths: [],
    skillGapAnalysis: null,
    isInitialized: false,
  });

  const getAuthToken = useCallback((): string | null => {
    return localStorage.getItem('user_auth_token') || localStorage.getItem('admin_auth_token');
  }, []);

  // Load persisted state from DB on mount
  useEffect(() => {
    if (!profileId || state.isInitialized) return;

    const loadState = async () => {
      try {
        // Load latest active resume analysis
        const { data: analysis } = await (supabase as any)
          .from('studio_resume_analyses')
          .select('*')
          .eq('user_id', profileId)
          .eq('is_active', true)
          .order('analyzed_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        // PROVENANCE GUARD: only hydrate if the loaded analysis matches the
        // currently-uploaded resume path. Prevents showing previous resume's
        // analysis after a re-upload (race between upload + background analyze).
        const currentResumePath = localStorage.getItem('studio_resume_path');
        const analysisMatchesCurrentFile =
          analysis && (!currentResumePath || analysis.file_path === currentResumePath);

        // Load selected career paths
        const { data: paths } = await (supabase as any)
          .from('studio_career_path_selections')
          .select('*')
          .eq('user_id', profileId)
          .eq('is_selected', true)
          .limit(50);

        // Load latest skill gap analysis
        const { data: skillGap } = await (supabase as any)
          .from('studio_skill_gap_analyses')
          .select('*')
          .eq('user_id', profileId)
          .order('analyzed_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        // Transform DB row back to AI response shape for ResumeAnalysisPage
        const resumeAnalysisData = (analysis && analysisMatchesCurrentFile) ? {
          overallScore: analysis.overall_score,
          atsScore: analysis.ats_score,
          keywordMatch: analysis.keyword_match,
          formatScore: analysis.format_score,
          grammarClarity: analysis.grammar_clarity,
          tonalityScore: analysis.tonality_score,
          humanWrittenScore: analysis.human_written_score,
          careerGrowthScore: analysis.career_growth_score,
          candidateInfo: analysis.candidate_info || {},
          extractedSkills: analysis.extracted_skills || { technical: [], soft: [], certifications: [], other: [] },
          matrixItems: analysis.matrix_items || [],
          careerGrowthAreas: analysis.career_growth_areas || [],
          aspirationPrediction: analysis.aspiration_prediction || {},
          aiDetection: analysis.ai_detection || {},
          behavioralInsights: analysis.behavioral_insights || [],
          strengths: analysis.strengths || [],
          improvements: analysis.improvements || [],
          actionPlan: analysis.action_plan || [],
          criticFeedback: analysis.critic_feedback || null,
          matchedCareerPaths: analysis.matched_career_paths || null,
          analysisId: analysis.id,
          analyzedAt: analysis.analyzed_at || analysis.created_at || null,
          analysisFilePath: analysis.file_path || null,
          file_path: analysis.file_path || null,
        } : null;

        if (analysis && !analysisMatchesCurrentFile) {
          console.log('[StudioContext] Skipping stale analysis hydration: analysis file_path does not match current resume path', {
            analysisPath: analysis.file_path,
            currentPath: currentResumePath,
          });
        }

        // Make sure email/phone/linkedin from columns enrich candidateInfo
        if (resumeAnalysisData && analysis) {
          resumeAnalysisData.candidateInfo = {
            ...resumeAnalysisData.candidateInfo,
            email: resumeAnalysisData.candidateInfo.email || analysis.candidate_email || '',
            phone: resumeAnalysisData.candidateInfo.phone || analysis.candidate_phone || '',
            linkedIn: resumeAnalysisData.candidateInfo.linkedIn || analysis.candidate_linkedin || '',
          };
        }

        setState({
          // Prefer the user's most recently uploaded path (localStorage) over a
          // potentially stale DB row's path so the analysis page re-runs against
          // the correct file when there's a mismatch.
          resumePath: localStorage.getItem('studio_resume_path') || analysis?.file_path || null,
          resumeAnalysis: resumeAnalysisData,
          selectedCareerPaths: paths || [],
          skillGapAnalysis: skillGap || null,
          isInitialized: true,
        });
      } catch (err) {
        console.error('[StudioContext] Failed to load state:', err);
        setState(prev => ({ ...prev, isInitialized: true }));
      }
    };

    loadState();
  }, [profileId, state.isInitialized]);

  const setResumePath = useCallback((path: string | null) => {
    setState(prev => ({ ...prev, resumePath: path }));
  }, []);

  const setResumeAnalysis = useCallback((analysis: any) => {
    setState(prev => ({ ...prev, resumeAnalysis: analysis }));
  }, []);

  const setSelectedCareerPaths = useCallback((paths: any[]) => {
    setState(prev => ({ ...prev, selectedCareerPaths: paths }));
  }, []);

  const setSkillGapAnalysis = useCallback((analysis: any) => {
    setState(prev => ({ ...prev, skillGapAnalysis: analysis }));
  }, []);

  return (
    <StudioContext.Provider value={{
      ...state,
      setResumePath,
      setResumeAnalysis,
      setSelectedCareerPaths,
      setSkillGapAnalysis,
      getAuthToken,
      profileId,
    }}>
      {children}
    </StudioContext.Provider>
  );
};
